import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Plus, ShieldCheck, UserPlus, Users } from "lucide-react";
import { useState } from "react";

import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Input, Label } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { Tabs } from "@/components/ui/tabs";
import { useSession } from "@/hooks/useSession";
import { api, type User } from "@/lib/api";
import { cn, fmtAgo } from "@/lib/utils";

interface Secret {
  id: string; name: string; kind: string; created_by: string;
  created_at: string; rotated_at: string | null;
}

const ROLE_DESC: Record<string, string> = {
  owner: "Full control — users, secrets, destructive actions",
  operator: "Operate apps, exceptions, bans — no user admin",
  viewer: "Read-only dashboard",
};

const ROLE_BADGE: Record<string, "default" | "warning" | "secondary"> = {
  owner: "default", operator: "warning", viewer: "secondary",
};

function UsersTab() {
  const qc = useQueryClient();
  const { user: me } = useSession();
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState("");
  const [f, setF] = useState({ email: "", name: "", password: "", role: "viewer" });

  const { data: users, error } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get<User[]>("/api/auth/users"),
    retry: false,
  });
  const create = useMutation({
    mutationFn: () => api.post("/api/auth/users", f),
    onSuccess: () => {
      setOpen(false); setF({ email: "", name: "", password: "", role: "viewer" });
      qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e) => setErr(e.message),
  });
  const patch = useMutation({
    mutationFn: ({ id, ...body }: { id: string; role?: string; is_active?: boolean }) =>
      api.patch(`/api/auth/users/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });

  if (error) {
    return (
      <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">
        User administration requires the owner role.
      </CardContent></Card>
    );
  }
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-base"><Users className="h-4 w-4" /> Users & roles</CardTitle>
          <CardDescription>Server-side sessions, Argon2id passwords, optional TOTP per user.</CardDescription>
        </div>
        <Button size="sm" onClick={() => setOpen(true)}><UserPlus className="h-4 w-4" /> Add user</Button>
      </CardHeader>
      <Table>
        <THead>
          <TR><TH>User</TH><TH>Role</TH><TH>MFA</TH><TH>Status</TH><TH>Role capabilities</TH><TH></TH></TR>
        </THead>
        <TBody>
          {(users ?? []).map((u) => (
            <TR key={u.id}>
              <TD>
                <div className="text-sm font-medium">{u.name || u.email.split("@")[0]}</div>
                <div className="text-xs text-muted-foreground">{u.email}</div>
              </TD>
              <TD><Badge variant={ROLE_BADGE[u.role]}>{u.role}</Badge></TD>
              <TD className="text-xs">
                {u.totp_enabled
                  ? <span className="text-emerald-300">TOTP on</span>
                  : <span className="text-muted-foreground">off</span>}
              </TD>
              <TD className="text-xs">
                {u.is_active
                  ? <span className="text-emerald-300">active</span>
                  : <span className="text-red-300">disabled</span>}
              </TD>
              <TD className="max-w-[280px] text-xs text-muted-foreground">{ROLE_DESC[u.role]}</TD>
              <TD>
                {u.id !== me?.id && (
                  <div className="flex items-center gap-2">
                    <Select
                      value={u.role}
                      onChange={(e) => patch.mutate({ id: u.id, role: e.target.value })}
                      className="h-7 w-28 text-xs"
                    >
                      <option value="owner">owner</option>
                      <option value="operator">operator</option>
                      <option value="viewer">viewer</option>
                    </Select>
                    <Button size="sm" variant="ghost" className="text-xs"
                      disabled={patch.isPending}
                      onClick={() => patch.mutate({ id: u.id, is_active: !u.is_active })}>
                      {u.is_active ? "disable" : "enable"}
                    </Button>
                  </div>
                )}
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} title="Add user"
        description="They'll sign in with email + password; TOTP is self-enrolled in Settings → Security.">
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); setErr(""); create.mutate(); }}>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Display name</Label>
              <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
                <option value="viewer">viewer</option>
                <option value="operator">operator</option>
                <option value="owner">owner</option>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Password (min 12 chars)</Label>
            <Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })}
              minLength={12} required />
            <p className="text-xs text-muted-foreground">{ROLE_DESC[f.role]}</p>
          </div>
          {err && <p className="text-sm text-destructive">{err}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button disabled={create.isPending}>{create.isPending ? "Creating…" : "Create user"}</Button>
          </div>
        </form>
      </Dialog>
    </Card>
  );
}

function SecurityTab() {
  const { user, refetch } = useSession();
  const qc = useQueryClient();
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwMsg, setPwMsg] = useState("");
  const [totpData, setTotpData] = useState<{ secret: string; uri: string } | null>(null);
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const [totpMsg, setTotpMsg] = useState("");

  const changePw = useMutation({
    mutationFn: () => api.post("/api/auth/password", { current_password: pw.current, new_password: pw.next }),
    onSuccess: () => { setPw({ current: "", next: "", confirm: "" }); setPwMsg("password changed"); },
    onError: (e) => setPwMsg(e.message),
  });
  const enroll = useMutation({
    mutationFn: () => api.post<{ secret: string; uri: string }>("/api/auth/totp/enroll"),
    onSuccess: setTotpData,
    onError: (e) => setTotpMsg(e.message),
  });
  const confirm = useMutation({
    mutationFn: () => api.post<{ recovery_codes: string[] }>("/api/auth/totp/confirm", { code }),
    onSuccess: (r) => {
      setRecovery(r.recovery_codes); setTotpData(null); setCode("");
      refetch(); qc.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (e) => setTotpMsg(e.message),
  });
  const disable = useMutation({
    mutationFn: () => api.del("/api/auth/totp"),
    onSuccess: () => { setRecovery(null); refetch(); },
    onError: (e) => setTotpMsg(e.message),
  });

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><KeyRound className="h-4 w-4" /> Password</CardTitle>
          <CardDescription>Argon2id — change takes effect next request.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-3" onSubmit={(e) => {
            e.preventDefault();
            setPwMsg(pw.next !== pw.confirm ? "passwords don't match" : "");
            if (pw.next === pw.confirm) changePw.mutate();
          }}>
            <div className="space-y-1.5">
              <Label>Current password</Label>
              <Input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>New password</Label>
                <Input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} minLength={12} required />
              </div>
              <div className="space-y-1.5">
                <Label>Confirm</Label>
                <Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} required />
              </div>
            </div>
            <Button size="sm" disabled={changePw.isPending}>Change password</Button>
            {pwMsg && <p className={cn("text-xs", pwMsg === "password changed" ? "text-emerald-300" : "text-destructive")}>{pwMsg}</p>}
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-4 w-4" /> Two-factor (TOTP)
            {user?.totp_enabled && <Badge variant="success">enabled</Badge>}
          </CardTitle>
          <CardDescription>Authenticator-app codes + one-time recovery codes.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!user?.totp_enabled && !totpData && !recovery && (
            <Button size="sm" variant="outline" onClick={() => enroll.mutate()} disabled={enroll.isPending}>
              Enroll authenticator
            </Button>
          )}
          {totpData && (
            <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); setTotpMsg(""); confirm.mutate(); }}>
              <div>
                <Label>Secret (add to authenticator)</Label>
                <code className="mt-1 block break-all rounded bg-muted/50 px-3 py-2 font-mono text-xs">{totpData.secret}</code>
                <p className="mt-1 break-all text-[11px] text-muted-foreground">{totpData.uri}</p>
              </div>
              <div className="space-y-1.5">
                <Label>Verify code</Label>
                <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" required />
              </div>
              <Button size="sm" disabled={confirm.isPending}>Confirm & enable</Button>
            </form>
          )}
          {recovery && (
            <div>
              <Label>Recovery codes — store once, never shown again</Label>
              <div className="mt-2 grid grid-cols-2 gap-1.5">
                {recovery.map((c) => (
                  <code key={c} className="rounded bg-muted/50 px-2 py-1 font-mono text-xs">{c}</code>
                ))}
              </div>
            </div>
          )}
          {user?.totp_enabled && !recovery && (
            <Button size="sm" variant="outline" onClick={() => disable.mutate()} disabled={disable.isPending}>
              Disable TOTP
            </Button>
          )}
          {totpMsg && <p className="text-xs text-destructive">{totpMsg}</p>}
        </CardContent>
      </Card>
    </div>
  );
}

function SecretsTab() {
  const qc = useQueryClient();
  const [f, setF] = useState({ name: "", kind: "generic", value: "" });
  const [msg, setMsg] = useState("");
  const { data: secrets, error } = useQuery({
    queryKey: ["secrets"],
    queryFn: () => api.get<Secret[]>("/api/secrets"),
    retry: false,
  });
  const add = useMutation({
    mutationFn: () => api.post("/api/secrets", f),
    onSuccess: () => {
      setF({ name: "", kind: "generic", value: "" });
      setMsg("stored — value is write-only from here");
      qc.invalidateQueries({ queryKey: ["secrets"] });
    },
    onError: (e) => setMsg(e.message),
  });

  if (error) {
    return (
      <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">
        Secret storage requires the owner role.
      </CardContent></Card>
    );
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><Plus className="h-4 w-4" /> Integration secrets</CardTitle>
        <CardDescription>
          Envelope-encrypted at rest (Fernet keyfile outside the DB). Values are
          write-only — even owners cannot read them back through the API.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Table>
          <THead><TR><TH>Name</TH><TH>Kind</TH><TH>Created by</TH><TH>Age</TH></TR></THead>
          <TBody>
            {(secrets ?? []).map((s) => (
              <TR key={s.id}>
                <TD className="font-mono text-xs">{s.name}</TD>
                <TD className="text-xs">{s.kind}</TD>
                <TD className="text-xs">{s.created_by}</TD>
                <TD className="text-xs">{fmtAgo(s.created_at)}</TD>
              </TR>
            ))}
            {(secrets ?? []).length === 0 && (
              <TR><TD colSpan={4} className="py-4 text-center text-muted-foreground">No secrets stored.</TD></TR>
            )}
          </TBody>
        </Table>
        <form
          onSubmit={(e) => { e.preventDefault(); setMsg(""); add.mutate(); }}
          className="grid gap-3 sm:grid-cols-4"
        >
          <div className="space-y-1.5"><Label>Name</Label>
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></div>
          <div className="space-y-1.5"><Label>Kind</Label>
            <Select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
              <option value="generic">generic</option>
              <option value="api_key">api key</option>
              <option value="crowdsec">crowdsec bouncer</option>
              <option value="pro_license">PRO license</option>
              <option value="smtp">smtp</option>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Value</Label>
            <Input type="password" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} required /></div>
          <div className="flex items-end"><Button size="sm" disabled={add.isPending}>Store</Button></div>
        </form>
        {msg && <p className="text-xs text-muted-foreground">{msg}</p>}
      </CardContent>
    </Card>
  );
}

function PlatformTab() {
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.get<Record<string, unknown>>("/api/settings"),
    retry: false,
  });
  const { data: caps } = useQuery({
    queryKey: ["capabilities"],
    queryFn: () => api.get<Record<string, unknown>>("/api/capabilities"),
  });
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Stored settings</CardTitle>
          <CardDescription>Controller-level settings.</CardDescription>
        </CardHeader>
        <CardContent>
          <pre className="max-h-72 overflow-auto rounded bg-muted/40 p-3 font-mono text-xs">
            {JSON.stringify(settings ?? {}, null, 2)}
          </pre>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Capabilities</CardTitle>
          <CardDescription>What this build reports.</CardDescription>
        </CardHeader>
        <CardContent>
          <pre className="max-h-72 overflow-auto rounded bg-muted/40 p-3 font-mono text-xs">
            {JSON.stringify(caps ?? {}, null, 2)}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}

interface IdProvider {
  id: string; name: string; type: string; enabled: boolean;
  config: Record<string, unknown>;
  created_by: string;
}

// Field hints per provider type — the API validates authoritatively; the
// form just collects the required keys. Secret values are always
// references to Secrets-tab entries, never literals.
const PROVIDER_FIELDS: Record<string, { key: string; hint: string; req?: boolean }[]> = {
  oidc: [
    { key: "issuer", hint: "https://idp.example.com/realms/x", req: true },
    { key: "client_id", hint: "client id", req: true },
    { key: "client_secret_ref", hint: "secrets-store entry name" },
    { key: "authorize_url", hint: "https://…/authorize", req: true },
    { key: "token_url", hint: "https://…/token", req: true },
    { key: "jwks_url", hint: "https://…/jwks.json", req: true },
    { key: "allowed_redirects", hint: `["${location.origin}/api/identity/callback"]`, req: true },
    { key: "role_map", hint: '{"idp-group":"operator"}' },
    { key: "default_role", hint: "viewer (empty = unmapped denied)" },
  ],
  saml: [
    { key: "idp_sso_url", hint: "https://idp…/sso", req: true },
    { key: "idp_entity_id", hint: "IdP entity ID", req: true },
    { key: "sp_entity_id", hint: "our SP entity ID", req: true },
    { key: "idp_cert_b64", hint: "base64 DER or PEM signing cert", req: true },
    { key: "role_map", hint: '{"admins":"operator"}' },
    { key: "default_role", hint: "viewer (empty = unmapped denied)" },
  ],
  ldap: [
    { key: "uri", hint: "ldaps://dir.example.com", req: true },
    { key: "starttls", hint: "true for ldap:// (required)", req: true },
    { key: "bind_dn", hint: "cn=nx-svc,dc=…", req: true },
    { key: "bind_secret_ref", hint: "secrets-store entry name", req: true },
    { key: "user_base", hint: "ou=users,dc=…", req: true },
    { key: "user_filter", hint: "(uid={username})", req: true },
    { key: "ca_cert_ref", hint: "secrets-store entry (optional)" },
    { key: "role_map", hint: '{"app-admins":"operator"}' },
    { key: "default_role", hint: "viewer (empty = unmapped denied)" },
  ],
};

function ProvidersTab() {
  const qc = useQueryClient();
  const [ptype, setPtype] = useState("oidc");
  const [name, setName] = useState("");
  const [fields, setFields] = useState<Record<string, string>>({});
  const [err, setErr] = useState("");
  const { user } = useSession();
  const canOperate = user?.role === "owner" || user?.role === "operator";

  const providers = useQuery({
    queryKey: ["idproviders"],
    queryFn: () => api.get<{ providers: IdProvider[] }>(
      "/api/identity/providers").catch(() => null),
  });
  const save = useMutation({
    mutationFn: () => {
      const config: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(fields)) {
        const t = v.trim();
        if (!t) continue;
        if (t.startsWith("[") || t.startsWith("{")) config[k] = JSON.parse(t);
        else if (t === "true" || t === "false") config[k] = t === "true";
        else config[k] = t;
      }
      return api.post("/api/identity/providers",
        { name: name.trim(), type: ptype, enabled: true, config });
    },
    onSuccess: () => {
      setErr(""); setName(""); setFields({});
      qc.invalidateQueries({ queryKey: ["idproviders"] });
    },
    onError: (e) => setErr(e.message),
  });
  const del = useMutation({
    mutationFn: (id: string) => api.del(`/api/identity/providers/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["idproviders"] }),
    onError: (e) => setErr(e.message),
  });
  const toggle = useMutation({
    mutationFn: (p: IdProvider) =>
      api.put(`/api/identity/providers/${p.id}`,
        { name: p.name, type: p.type, enabled: !p.enabled, config: p.config }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["idproviders"] }),
    onError: (e) => setErr(e.message),
  });

  if (providers.data === null) {
    return <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">
      nx.identity module disabled — enable it on the Modules page.
    </CardContent></Card>;
  }
  const list = providers.data?.providers ?? [];
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Identity providers</CardTitle>
          <CardDescription>
            OIDC (code+PKCE), SP-initiated SAML and TLS-verified LDAP.
            Configs carry no secrets — *ref fields name Secrets-tab entries.
            Accounts join by issuer+subject; email never auto-links.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {list.length === 0 && <p className="text-sm text-muted-foreground">No providers configured.</p>}
          {list.length > 0 && (
            <Table>
              <THead><TR><TH>name</TH><TH>type</TH><TH>state</TH><TH /></TR></THead>
              <TBody>
                {list.map((p) => (
                  <TR key={p.id}>
                    <TD className="font-medium">{p.name}</TD>
                    <TD className="font-mono text-xs">{p.type}</TD>
                    <TD><Badge variant={p.enabled ? "success" : "secondary"}>
                      {p.enabled ? "enabled" : "disabled"}</Badge></TD>
                    <TD className="text-right space-x-1">
                      {canOperate && (
                        <Button size="sm" variant="ghost" onClick={() => toggle.mutate(p)}>
                          {p.enabled ? "disable" : "enable"}
                        </Button>
                      )}
                      {user?.role === "owner" && (
                        <Button size="sm" variant="ghost" onClick={() => del.mutate(p.id)}>
                          delete
                        </Button>
                      )}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>
      {canOperate && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Add provider</CardTitle>
            <CardDescription>
              Fields marked * are required. JSON fields accept arrays/objects.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <Select value={ptype} onChange={(e) => { setPtype(e.target.value); setFields({}); }}>
                <option value="oidc">OIDC</option>
                <option value="saml">SAML</option>
                <option value="ldap">LDAP</option>
              </Select>
              <Input placeholder="display name" value={name}
                onChange={(e) => setName(e.target.value)} />
            </div>
            {PROVIDER_FIELDS[ptype].map((f) => (
              <div key={f.key} className="space-y-1">
                <Label>{f.key}{f.req ? " *" : ""}</Label>
                <Input placeholder={f.hint}
                  value={fields[f.key] ?? ""}
                  onChange={(e) => setFields({ ...fields, [f.key]: e.target.value })} />
              </div>
            ))}
            {err && <p className="text-sm text-destructive">{err}</p>}
            <Button size="sm" onClick={() => save.mutate()}
              disabled={save.isPending || !name.trim()}>
              create provider
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function Settings() {
  const { user } = useSession();
  const [tab, setTab] = useState("security");
  return (
    <div className="space-y-5">
      <PageHeader title="Settings" description="Account security, user administration, secrets and platform configuration." />
      <Tabs value={tab} onChange={setTab} tabs={[
        { value: "security", label: "Security" },
        ...(user?.role === "owner" || user?.role === "operator" ? [
          { value: "identity", label: "Identity providers" },
        ] : []),
        ...(user?.role === "owner" ? [
          { value: "users", label: "Users & roles" },
          { value: "secrets", label: "Secrets" },
        ] : []),
        { value: "platform", label: "Platform" },
      ]} />
      {tab === "security" && <SecurityTab />}
      {tab === "identity" && <ProvidersTab />}
      {tab === "users" && <UsersTab />}
      {tab === "secrets" && <SecretsTab />}
      {tab === "platform" && <PlatformTab />}
    </div>
  );
}
